import * as React from "react"

import { cn } from "@/lib/utils"

function Card({
  className,
  size = "default",
  ...props
}: React.ComponentProps<"div"> & { size?: "default" | "sm" }) {
  return (
    <div
      data-slot="card"
      data-size={size}
      className={cn(
        // `border` is load-bearing and must stay UNCONDITIONAL. Tailwind's
        // `border-{color}` utilities set border-COLOR only, never width, and v4
        // preflight defaults border-width to 0 — so when the Create redesign
        // swapped the base `border` for `ring-1 ring-foreground/10`, all 41 of
        // the 56 public <Card> call sites that pass a colour-only override
        // (`border-border/40`, `border-[var(--roma-parchment-line)]`,
        // `border-border/60`) silently rendered NO border, replaced by a
        // foreground-tinted ring that ignores brand colour entirely. That hit
        // order/checkout (9 instances), order/confirmation (5), the reservation
        // form, and all three private-events/catering lead pages (17). The 15
        // `.roma-card` instances escaped only because that class is unlayered
        // CSS in tuscan-globals.css and wins the cascade outright — luck, not
        // design.
        //
        // Create's card treatment is gated behind `data-ops-shell` (see the
        // matching note in ui/button.tsx for why that attribute and not
        // `.admin-theme`). Admin keeps the ring/shadow-xs/text-sm it was
        // designed with; public keeps the border + shadow-sm it shipped with.
        "group/card flex flex-col gap-(--card-spacing) overflow-hidden rounded-xl border bg-card py-(--card-spacing) text-card-foreground shadow-sm in-data-[ops-shell]:border-0 in-data-[ops-shell]:text-sm in-data-[ops-shell]:shadow-xs in-data-[ops-shell]:ring-1 in-data-[ops-shell]:ring-foreground/10 [--card-spacing:--spacing(6)] has-[>img:first-child]:pt-0 data-[size=sm]:[--card-spacing:--spacing(4)] *:[img:first-child]:rounded-t-xl *:[img:last-child]:rounded-b-xl",
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        // gap-2 was the pre-branch public value; Create's gap-1 is admin-gated.
        // Visible wherever a header stacks Title + Description — on public that
        // is the rewards upsell on order/confirmation.
        "group/card-header @container/card-header grid auto-rows-min items-start gap-2 rounded-t-xl px-(--card-spacing) in-data-[ops-shell]:gap-1 has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-(--card-spacing)",
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        // font-semibold was the pre-branch public weight; 4 public CardTitles on
        // order/confirmation set no weight of their own and were de-emphasised by
        // Create's font-medium. Admin keeps the lighter weight.
        "text-base leading-normal font-semibold in-data-[ops-shell]:font-medium group-data-[size=sm]/card:text-sm",
        className
      )}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-(--card-spacing)", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "flex items-center rounded-b-xl px-(--card-spacing) [.border-t]:pt-(--card-spacing)",
        className
      )}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
}
